import { useMemo, useState } from "react";
import Joi from "joi";
import { Stack, VStack, Text, Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import FormRadio from "components/Chakra/FormRadio";
import { NewWDType } from "scenes/Watchdog/types";
import { EApiWatchdogFrequencyId } from "utils/watchdog";
import Location2JsonApiEndpoint from "utils/geo/api/Location2Json";
import NumericInterval from "utils/NumericInterval";
import { DISPOSITION_MAP } from "@ud/config/dispositions";
import { DEFAULT_OFFER_AGE } from "@ud/config/offerAge";
import { DEFAULT_OFFER_SORTING } from "@ud/config/offerSorting";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Button from "components/Chakra/Button";
import { useLoginModal } from "modals/useLoginModal";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import useWatchdogs from "hooks/useWatchdogs";
import { TCreateWatchDogInput } from "utils/watchdog/api/CreateWatchdog";

interface IFormValues {
  name: string;
}

const formSchema = Joi.object({
  name: Joi.string().required(),
});

interface IProps {
  watchdog: NewWDType;
  onSubmit?: () => void;
}

const Step3Form = ({ watchdog, onSubmit }: IProps): JSX.Element => {
  const [frequencyId, setFrequencyId] = useState<string>(
    EApiWatchdogFrequencyId.ONCE_PER_DAY,
  );
  const { user } = useGlobalState();
  const { openModal: openLoginModal } = useLoginModal();
  const { t } = useTranslation();
  const { createDog } = useWatchdogs();

  const { callEndpoint: location2JsonApi } = useApiEndpoint(
    Location2JsonApiEndpoint,
  );

  const frequencyOptions = useMemo(
    () => [
      {
        value: EApiWatchdogFrequencyId.IMMEDIATE,
        label: t("watchdogFrequency.immediate"),
        badge: "Nejoblíbenější",
      },
      {
        value: EApiWatchdogFrequencyId.ONCE_PER_DAY,
        label: t("watchdogFrequency.oncePerDay"),
      },
      {
        value: EApiWatchdogFrequencyId.ONCE_PER_WEEK,
        label: t("watchdogFrequency.oncePerWeek"),
      },
    ],
    [t],
  );

  const { handleSubmit, registerWithError } = useForm<IFormValues>(formSchema, {
    defaultValues: {
      name: [
        watchdog.location,
        watchdog.dispositions
          .map((disposition) => disposition.translationId)
          .join(", "),
      ].join(", "),
    },
    keepDataOnSuccess: true,
  });

  const handleData: OnFormSubmitType<IFormValues> = async (
    data: IFormValues,
  ) => {
    const { location, price, dispositions } = watchdog;

    const finishDog = async (): Promise<void> => {
      const { data: locationData } = await location2JsonApi({ location });
      if (!user || !locationData) {
        return;
      }

      const dataCreateDog: TCreateWatchDogInput = {
        email: user.email,
        frequencyId,
        name: data.name,
        filter: {
          price,
          dispositions: dispositions.filter((disposition) =>
            Boolean(DISPOSITION_MAP.get(disposition.queryValue)),
          ),
          furnishing: [],
          conveniences: [],
          isBalcony: false,
          area: new NumericInterval(),
          bounds: locationData.bounds,
          noCommitionFee: null,
          specialFlag: null,
          offerAge: DEFAULT_OFFER_AGE,
          sorting: DEFAULT_OFFER_SORTING,
          pageIndex: 1,
        },
        offerTypeId: 1,
        location,
      };

      if (
        !user.premiumTill &&
        frequencyId === EApiWatchdogFrequencyId.IMMEDIATE
      ) {
        const localWdData = localStorage.getItem("wd-immediate");
        !!localWdData &&
          localStorage.setItem("wd-immediate", JSON.stringify(dataCreateDog));
      }

      createDog(dataCreateDog, onSubmit);
    };

    if (!user) {
      openLoginModal({
        onSuccessfulLogin: finishDog,
        loginEvent: {
          LOGIN: {
            "dl.eAct": "login",
            "dl.eLab": "Hlídací pes",
            "dl.eCat": "landing",
          },
          REGISTRATION: {
            "dl.eCat": "landing",
            "dl.eAct": "registration",
            "dl.eLab": "Hlídací pes",
          },
        },
      });
    } else {
      finishDog();
    }
  };

  return (
    <VStack
      data-test="3stepFormDog"
      as="form"
      onSubmit={handleSubmit(handleData)}
      spacing={4}
      background="ud-white"
      borderRadius={6}
      boxShadow="0 0 10px 0 rgba(0, 0, 0, 0.19)"
      paddingY={12}
      paddingX={{ base: 5, sm: 12 }}
    >
      <AnimatedInput
        {...registerWithError("name")}
        label="Jméno"
        required
        data-test="dogNameInput"
      />
      <Stack spacing={4} w="100%" data-test="intervalOfNotificationToggle">
        <Text variant="small_1" textTransform="uppercase" fontWeight="500">
          Interval upozornění
        </Text>
        <VStack spacing={1} alignItems="left">
          <Box
            borderWidth="1px"
            borderStyle="solid"
            borderColor="ud-grey-300"
            borderRadius={4}
            padding={4}
            w="100%"
          >
            <FormRadio
              data-test="karel"
              items={frequencyOptions}
              value={frequencyId}
              onChange={setFrequencyId}
            />
          </Box>
        </VStack>
      </Stack>

      <Button
        data-test="completeButton"
        type="submit"
        width="max-content"
        trackEventPayload={{
          "dl.eCat": "landing",
          "dl.eAct": "watchdog.clicked",
          "dl.eLab": "WD 3. krok interval",
        }}
      >
        Dokončit
      </Button>
    </VStack>
  );
};

export default Step3Form;
